import { CommonModule, NgFor, NgStyle } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgApexchartsModule } from 'ng-apexcharts';
import {
  ApexAxisChartSeries,
  ApexChart,
  ChartComponent,
  ApexDataLabels,
  ApexPlotOptions,
  ApexYAxis,
  ApexTitleSubtitle,
  ApexXAxis,
  ApexFill
} from "ng-apexcharts";

import { ToastrService } from 'ngx-toastr';
import { DashboardService } from '../../../Services/dashboard.service';
import { MasterInformationService } from '../../../Services/master-information.service';
import { apiEndPoints } from '../../../api/apiEndPoint';
import { ReportsService } from '../../../Services/reports.service';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  fill: ApexFill;
  title: ApexTitleSubtitle;
  colors: string[];
  stroke: any
};


@Component({
  selector: 'app-revenue-dashboard',
  standalone: true,
  imports: [NgFor, FormsModule, CommonModule, NgApexchartsModule],
  templateUrl: './revenue-dashboard.component.html',
  styleUrls: ['./revenue-dashboard.component.css'],

})
export class RevenueDashboardComponent implements OnInit {

  cityId: string = ""

  zoneId: string = ""

  year: string = ""

  years: number[] = [2025];

  summary: any = {}

  monthlyData: any[] = []

  cities: any[] = [];

  zones: any[] = [];

  totalTons: number[] = []

  totalTripCount: number[] = []
  isToDateShow: boolean = false;
  fromDate: any;
  toDate: any;
  orderSummaryData: any;
  isTableVisible: boolean = false;

  constructor(private dashboardService: DashboardService,
    private masterService: MasterInformationService,
    private toastr: ToastrService,
    private reportService: ReportsService,
    private cdRef: ChangeDetectorRef
  ) { }


  ngOnInit(): void {
    this.getCities()
    this.getSalesDashboardData()
    this.getOrderSummary();
  }

  onCityChange() {
    this.getZones();
    this.getSalesDashboardData()
  }

  onZoneChange() {
    this.getSalesDashboardData()
  }

  onYearChange() {
    this.getSalesDashboardData()
  }

  // ------------------------------ Dumpster & Dump yard --------------------------- //

  chartSeries = [
    {
      name: 'SAR allocated to the dumpster',
      data: [30, 40, 45, 50, 49, 60, 70, 91, 125, 120, 140, 150]
    },
    {
      name: 'SAR allocated to the dump yard',
      data: [20, 30, 35, 40, 39, 50, 60, 81, 115, 110, 120, 130]
    },
    {
      name: 'SAR allocated to the smart-sort',
      data: [30, 40, 45, 50, 49, 60, 70, 91, 125, 120, 140, 150]
    },
  ];

  toggleTableVisibility(event: any) {
    console.log('Chart clicked', event);
    this.isTableVisible = !this.isTableVisible; // Toggle table visibility
    this.cdRef.detectChanges();
  }


  chartOptions = {
    chart: {
      type: 'bar' as const, // Use strict types from ApexCharts
      height: 350,
      stacked: false,
      toolbar: {
        show: false
      },
    },
    xaxis: {
      categories: [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ],
      title: {
        text: 'Months'
      }
    },
    yaxis: {
      title: {
        text: 'Values'
      }
    },
    legend: {
      position: 'top' as const,
      horizontalAlign: 'left' as const
    },
    colors: ['#008FFB', '#FF4560', '#7fb519'], // Custom colors for bars
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        endingShape: "rounded"
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"]
    },
  };

  // ------------------------------ Smart-sort --------------------------- //

  chartSingleSeries = [
    {
      name: 'Total containers achieved',
      data: []
    },
    {
      name: 'Total waste disposed',
      data: []
    },
  ];

  chartSingleOptions = {
    chart: {
      type: 'bar' as const, // Use strict types from ApexCharts
      height: 350,
      stacked: false,
      toolbar: {
        show: false
      },
      events: {
        dataPointSelection: (event: any, chartContext: any, config: any) => {
          this.toggleTableVisibility(config);
        }
      }
    },
    xaxis: {
      categories: [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ],
      title: {
        text: 'Months'
      }
    },
    yaxis: {
      title: {
        text: 'Values'
      }
    },
    legend: {
      position: 'top' as const,
      horizontalAlign: 'left' as const
    },
    colors: ['#3e1a33', '#FFBF00'], // Custom colors for bars
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        endingShape: "rounded"
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"]
    },
  };

  getSalesDashboardData() {
    this.dashboardService.getSalesDashboardData(apiEndPoints.salesDashboardData, this.cityId,
      this.zoneId, this.year, this.fromDate, this.toDate).subscribe({
        next: (response: any) => {
          let totalTons: number[] = []
          let totalTripCount: number[] = []
          this.summary = response.body.details.summary;
          this.monthlyData = response.body.details.monthlyData;

          this.monthlyData.forEach((data: any) => {
            totalTons.push(data.monthlyTotalTons)
            totalTripCount.push(data.monthlyTripCount)
          })

          let series: any[] = [
            {
              name: 'Total containers achieved',
              data: [...totalTons]
            },
            {
              name: 'Total waste disposed',
              data: [...totalTripCount]
            },
          ]

          this.chartSingleSeries = series
        },

        error: (err) => {
          this.toastr.error('An unexpected error occurred. Please try again later.', 'Error', {
            timeOut: 3000,
            positionClass: 'toast-top-right',
            progressBar: true
          });
        }
      })
  }

  getCities() {
    this.masterService.getCityList(apiEndPoints.cityList).subscribe({
      next: (response: any) => {
        this.cities = response.body.details;
      },
      error: (error) => {
        this.toastr.error('An unexpected error occurred. Please try again later.', 'Error', {
          timeOut: 3000,
          positionClass: 'toast-top-right',
          progressBar: true
        });
      }
    })
  }

  getZones() {
    this.dashboardService.getZoneList(apiEndPoints.zoneList, this.cityId).subscribe({
      next: (response: any) => {
        this.zones = response.body.details;
      },
      error: (error) => {
        this.toastr.error('An unexpected error occurred. Please try again later.', 'Error', {
          timeOut: 3000,
          positionClass: 'toast-top-right',
          progressBar: true
        });
      }
    })
  }

  onFromDateChange(event: any) {
    this.fromDate = event.target.value;
    this.isToDateShow = true;
  }

  onToDateChange(event: any) {
    this.toDate = event.target.value;
    this.getOrderSummary();
  }

  // ----------------------------------- Get Order Summary API ----------------------------------- //

  getOrderSummary() {

    const reqBody: any = {

    };

    if (this.fromDate) {
      const [fromYear, fromMonth, fromDay] = this.fromDate.split("-");
      reqBody.fromDay = fromDay;
      reqBody.fromMonth = fromMonth;
      reqBody.fromYear = fromYear;
    }

    if (this.toDate) {
      const [toYear, toMonth, toDay] = this.toDate.split("-");
      reqBody.toDay = toDay;
      reqBody.toMonth = toMonth;
      reqBody.toYear = toYear;
    }

    console.log("Request Body:", reqBody);

    this.reportService
      .getOrderSummaryByDate(apiEndPoints.OrderSummaryByDate, reqBody)
      .subscribe({
        next: (response: any) => {
          console.log("API Response:", response);
          if (response.isSuccess) {
            console.log("Order Summary Data:", response.body.details);
            this.orderSummaryData = response.body.details;
            this.cdRef.detectChanges();
          } else {
            console.warn("API response indicates failure:", response);
          }
        },
        error: (error: any) => {
          console.error("API call failed:", error);
        },
      });
  }
}
